import { Color, Emotion, Message } from "../Message";
import { Robot } from "../Robot";
import { generateId } from "@/helpers/utils";
import { PollyService, PollyVoice } from "@/services/PollyService";
import { findMatchingVoice } from "@/helpers/voice_helpers";

export class LocalRobot implements Robot {
  id: string;
  kind = "LocalRobot";
  errors = [];
  isOn: boolean;
  synth: SpeechSynthesis;
  voices: (SpeechSynthesisVoice | PollyVoice)[];
  selectedVoice: SpeechSynthesisVoice | PollyVoice | null;
  defaultVoice: SpeechSynthesisVoice | PollyVoice | null;

  constructor() {
    this.id = "local" + generateId();
    this.isOn = true;
    this.synth = window.speechSynthesis;
    this.voices = this.synth.getVoices();
    this.defaultVoice =
      this.voices.find((v) => v.default === true) || this.voices[0] || null;
    this.synth.onvoiceschanged = () => {
      const browserVoices = this.synth.getVoices();
      // Keep existing Polly voices if any
      const pollyVoices = this.voices.filter((v) =>
        v.voiceURI.startsWith("aws:"),
      ) as PollyVoice[];
      this.voices = [...browserVoices, ...pollyVoices];
      this.defaultVoice =
        this.voices.find((v) => v.default === true) || this.voices[0] || null;
    };
    this.selectedVoice = this.defaultVoice;
  }

  setVoices(voices: (SpeechSynthesisVoice | PollyVoice)[]) {
    if (voices.length > 0) {
      const currentVoiceStillExists = findMatchingVoice(
        voices,
        this.selectedVoice?.voiceURI,
      );

      if (currentVoiceStillExists) {
        this.selectedVoice = currentVoiceStillExists;
      } else {
        this.selectedVoice = voices.find((v) => v.default) || voices[0];
      }
    } else {
      this.selectedVoice = null;
    }
    this.voices = voices;
  }

  setVoice(voiceUri: string) {
    this.selectedVoice =
      findMatchingVoice(this.voices, voiceUri) || this.defaultVoice;
  }

  sendMessage(message: Message): void {
    if (!this.selectedVoice || !message.speech) return;

    if (this.selectedVoice.voiceURI.startsWith("aws:")) {
      this.speakPolly(message.speech);
    } else {
      const speech = new SpeechSynthesisUtterance(message.speech);
      speech.voice = this.selectedVoice as SpeechSynthesisVoice;
      this.synth.speak(speech);
    }
  }

  sendSpeech(speech: string): void {
    this.sendMessage({ speech } as Message);
  }

  private async speakPolly(text: string) {
    try {
      if (!this.selectedVoice) return;
      const parts = this.selectedVoice.voiceURI.split(":");
      const pollyVoiceId = parts[1];
      const engine = parts[2];

      const { audio: audioUrl } = await PollyService.synthesizeSpeech(
        text,
        pollyVoiceId,
        engine,
      );

      const audio = new Audio(audioUrl);
      audio.crossOrigin = "anonymous";
      await audio.play();
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
      };
    } catch (error) {
      console.error("LocalRobot Polly speech error", error);
    }
  }

  sendColor(color: Color): void {
    console.log(color);
  }

  sendEmotion(emotion: Emotion): void {
    console.log(emotion);
  }

  toString(): string {
    return JSON.stringify({
      kind: this.kind,
      voice: this.selectedVoice ? this.selectedVoice.voiceURI : "",
    });
  }

  toPrettyString(): string {
    return `Local Robot using voice ${this.selectedVoice?.name || "none"}`;
  }
}
